/**
 * Watching a campaign that is being sent somewhere else.
 *
 * Sending is asynchronous by design — an in-process scheduler behind the job
 * abstraction today, a queue in some later version — so a campaign that has just
 * been scheduled is `scheduled`, not sent, and no screen may assume otherwise.
 * This hook is how the detail screen finds out what happened: it re-reads
 * `GET /campaigns/:id` while the campaign can still move, and stops the moment
 * it cannot.
 *
 * **Why it re-reads the whole campaign.** The contract describes a
 * `GET /campaigns/:id/progress` carrying `campaignProgressSchema`; the API does
 * not implement one. Re-reading the campaign is heavier and is not wrong: every
 * number on the screen — the progress counts, the skip breakdown, the readiness,
 * the status — comes back on one request, so the poll cannot report a different
 * vocabulary than the page it is updating. If the narrower route arrives, this
 * file is the only one that changes.
 *
 * Three rules, the same three `use-dataset-status.ts` keeps:
 *
 *   - **It stops.** A terminal campaign is not polled at all, and neither is a
 *     draft. One that is scheduled for next Tuesday is not polled either — the
 *     hook sleeps until the time approaches and only then starts, so an open tab
 *     is not a slow load test against a campaign that will not move for days.
 *   - **It gives up out loud.** After {@link MAX_CONSECUTIVE_FAILURES} failed
 *     rounds it stops and reports the error, so a dead API shows as an error
 *     rather than as numbers that quietly stopped moving.
 *   - **Every number comes from the API.** Nothing here increments a counter or
 *     predicts the next value. The counts are two grouped aggregates computed on
 *     read, and a client-side guess at what they are about to become is exactly
 *     the stored-counter problem the contract rejected, one layer up.
 */
import { isCampaignTerminal, type Campaign, type CampaignStatus } from '@wend/shared';
import { useEffect, useRef, useState } from 'react';

import { isAbortError } from './api/abort';
import { fetchCampaign, type CampaignWithReadinessResponse } from './api/campaigns';

/** Fast, because a send of a few hundred messages visibly moves. */
export const SENDING_POLL_INTERVAL_MS = 2_000;

/** A campaign that is due but has not started yet: the scheduler polls too. */
export const DUE_POLL_INTERVAL_MS = 3_000;

/**
 * How long before its time a scheduled campaign starts being watched.
 *
 * The server's clock and the browser's disagree by seconds, and the scheduler
 * itself polls on an interval — so "due" has to be a window rather than an
 * instant, or the screen sits idle through the first few seconds of its own
 * send.
 */
export const SCHEDULE_LEAD_MS = 60_000;

/**
 * The longest single sleep before re-checking whether a campaign is due.
 *
 * A `setTimeout` longer than 2^31−1 ms overflows and fires immediately, which
 * would turn a campaign scheduled for next year into a busy loop. Re-arming
 * every few minutes costs nothing and cannot overflow.
 */
export const MAX_SLEEP_MS = 5 * 60_000;

/** Consecutive failed rounds before the hook stops and surfaces the error. */
const MAX_CONSECUTIVE_FAILURES = 5;

/** What the hook is doing, so the screen can say so rather than imply it. */
export interface CampaignWatch {
  /** A poll is running: the numbers on screen are being refreshed. */
  readonly watching: boolean;
  /**
   * The campaign is scheduled for a time far enough away that nothing is being
   * polled. The ISO instant it is waiting for, so the screen can say when
   * watching will begin instead of looking idle for no reason.
   */
  readonly sleepingUntil: string | null;
  /** Set when polling gave up. The screen says so instead of spinning on. */
  readonly error: unknown;
}

export type WatchMode =
  | { readonly kind: 'idle' }
  | { readonly kind: 'sleep'; readonly ms: number }
  | { readonly kind: 'poll'; readonly intervalMs: number };

/**
 * Whether this campaign is worth polling, and how often.
 *
 * Exported because it is the whole policy and it is a pure function of three
 * values — a hook that decides this inline is a hook whose stopping rule cannot
 * be read without reading the timer code around it.
 */
export function campaignWatchMode(
  status: CampaignStatus,
  scheduledAt: string | null,
  now: number,
): WatchMode {
  if (status === 'draft' || isCampaignTerminal(status)) return { kind: 'idle' };
  if (status === 'sending') return { kind: 'poll', intervalMs: SENDING_POLL_INTERVAL_MS };

  // A campaign paused for credits is waiting on a *person* — somebody has to top
  // the workspace up — and that can be days. Polling for it would be polling for
  // ever, so the screen offers a Refresh instead. (This arm was written in slice
  // 5 as a prediction; since slice 6 `campaign-send.runner.ts` writes the status
  // for real, so it is a live path rather than a placeholder.)
  if (status === 'paused_insufficient_credits') return { kind: 'idle' };

  // Slice 18's two states split on exactly the question this function is for,
  // and they split opposite ways — which is why they are two arms and not one.
  //
  // `holding_between_waves` is waiting on a *clock and a webhook*: the hold
  // elapses and Amazon's delivery reports arrive, both within minutes, and then
  // the next wave releases on its own. That is the same "waiting for the server"
  // case as a campaign that is due, so it polls on the same interval.
  //
  // `halted_delivery_risk` is waiting on a *person* — somebody has to look at
  // the list and resume it — and that can be days. It is `paused_insufficient_credits`'s
  // case exactly, so it takes that arm's answer: idle, with the screen offering
  // a Refresh rather than a poll that runs for ever.
  if (status === 'holding_between_waves') return { kind: 'poll', intervalMs: DUE_POLL_INTERVAL_MS };
  if (status === 'halted_delivery_risk') return { kind: 'idle' };

  // `scheduled`, and it may or may not carry a time: a campaign cannot reach
  // this status without one, but the shape allows it and a poll is the safe
  // reading of "waiting for the server, no idea when".
  if (scheduledAt === null) return { kind: 'poll', intervalMs: DUE_POLL_INTERVAL_MS };

  const due = Date.parse(scheduledAt);
  if (Number.isNaN(due)) return { kind: 'poll', intervalMs: DUE_POLL_INTERVAL_MS };

  const sleepFor = due - SCHEDULE_LEAD_MS - now;
  if (sleepFor <= 0) return { kind: 'poll', intervalMs: DUE_POLL_INTERVAL_MS };
  return { kind: 'sleep', ms: Math.min(sleepFor, MAX_SLEEP_MS) };
}

/**
 * @param campaign the campaign as the screen currently holds it, or null while
 *                 it is still loading. Only three of its fields are read, and
 *                 they are the three the stopping rule is made of.
 * @param onUpdate called with every fresh read. The screen replaces what it is
 *                 holding; nothing is merged, because a partial update is how a
 *                 progress bar ends up describing two different moments.
 */
export function useCampaignProgress(
  campaign: Pick<Campaign, 'id' | 'status' | 'scheduledAt'> | null,
  onUpdate: (response: CampaignWithReadinessResponse) => void,
): CampaignWatch {
  const [watching, setWatching] = useState(false);
  const [sleepingUntil, setSleepingUntil] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  // Bumped when a sleep ends, so the effect re-evaluates the mode against the
  // clock as it now is rather than as it was when the campaign last changed.
  const [wake, setWake] = useState(0);

  // `onUpdate` is a fresh closure every render; the timer must not restart
  // because of that, so the latest one is read from a ref when it is needed.
  const onUpdateRef = useRef(onUpdate);
  useEffect(() => {
    onUpdateRef.current = onUpdate;
  });

  const campaignId = campaign?.id ?? null;
  const status = campaign?.status ?? null;
  const scheduledAt = campaign?.scheduledAt ?? null;

  useEffect(() => {
    if (campaignId === null || status === null) return;

    const mode = campaignWatchMode(status, scheduledAt, Date.now());

    if (mode.kind === 'idle') {
      setWatching(false);
      setSleepingUntil(null);
      setError(null);
      return;
    }

    if (mode.kind === 'sleep') {
      setWatching(false);
      setSleepingUntil(scheduledAt);
      setError(null);

      const sleep = window.setTimeout(() => {
        setWake((value) => value + 1);
      }, mode.ms);

      return () => {
        window.clearTimeout(sleep);
      };
    }

    setWatching(true);
    setSleepingUntil(null);

    const controller = new AbortController();
    let stopped = false;
    let failures = 0;
    let timer: number | undefined;

    const schedule = (): void => {
      if (stopped) return;
      timer = window.setTimeout(() => {
        void round();
      }, mode.intervalMs);
    };

    const round = async (): Promise<void> => {
      try {
        const response = await fetchCampaign(campaignId, controller.signal);
        if (stopped) return;

        failures = 0;
        setError(null);
        // If this read changed the status, the effect tears itself down and
        // starts again in the right mode — including stopping altogether once
        // the campaign reaches `sent`.
        onUpdateRef.current(response);
        schedule();
      } catch (cause) {
        // The unmount already reported itself through `stopped`; an abort here
        // is this hook's own doing and is not a failure of the send.
        if (stopped || isAbortError(cause)) return;

        failures += 1;
        if (failures >= MAX_CONSECUTIVE_FAILURES) {
          stopped = true;
          setWatching(false);
          setError(cause);
          return;
        }
        schedule();
      }
    };

    schedule();

    return () => {
      stopped = true;
      if (timer !== undefined) window.clearTimeout(timer);
      controller.abort();
    };
  }, [campaignId, status, scheduledAt, wake]);

  return { watching, sleepingUntil, error };
}
